import React, { useState } from 'react';
import { Button, Grid, Column, Tile, Accordion, AccordionItem } from '@carbon/react';
import { Checkmark, ArrowRight, Calculator, Question } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import PricingHero from '../components/pricing/PricingHero';
import PricingTier from '../components/pricing/PricingTier';
import UsagePricing from '../components/pricing/UsagePricing';
import { useAudUsdFxRate } from '../hooks/useFxRate';

const PricingPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { rate: audUsdRate } = useAudUsdFxRate();
  const [activeTab, setActiveTab] = useState('plans');
  const [billingInterval, setBillingInterval] = useState('monthly');

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const usdParen = (audAmount, { maximumFractionDigits = 0 } = {}) => {
    if (typeof audUsdRate !== 'number' || !Number.isFinite(audUsdRate)) return '';
    if (typeof audAmount !== 'number' || !Number.isFinite(audAmount)) return '';
    const usd = audAmount * audUsdRate;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits,
    }).format(usd);
    return ` (${formatted} USD)`;
  };

  const isYearly = billingInterval === 'yearly';
  const yearlyDiscount = 0.20;
  const getPrice = (monthlyPrice) => {
    if (isYearly) {
      return Math.round(monthlyPrice * 12 * (1 - yearlyDiscount));
    }
    return monthlyPrice;
  };

  const pricingTiers = {
    free: {
      name: "Free",
      basePrice: 0,
      monthlyEquivalent: null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: 1,
      gatewayCount: 1,
      gatewayLabel: "1 gateway",
      description: "Try ChillVerify with one sensor and one gateway",
      features: [
        "Connect 1 sensor",
        "1 gateway",
        "Real-time temperature alerts",
        "30-day data history",
        "Mobile app access",
        "Email support"
      ],
      cta: "Get Started",
      popular: false,
      free: true
    },
    starter: {
      name: "Starter",
      basePrice: isYearly ? getPrice(29) : 29,
      monthlyEquivalent: isYearly ? Math.round(29 * (1 - yearlyDiscount)) : null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: 3,
      gatewayCount: 2,
      gatewayLabel: "2 gateways",
      description: "Perfect for local butchers and small operations",
      features: [
        "Connect up to 3 sensors",
        `Add more sensors for $5 AUD / sensor / month${usdParen(5)}`,
        "2 gateways",
        "Mobile app access",
        "Real-time temperature alerts",
        "30-day data history",
        "Email support"
      ],
      cta: "Get Started",
      popular: false
    },
    business: {
      name: "Business",
      basePrice: isYearly ? getPrice(75) : 75,
      monthlyEquivalent: isYearly ? Math.round(75 * (1 - yearlyDiscount)) : null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: 10,
      gatewayCount: 7,
      gatewayLabel: "7 gateways",
      description: "Ideal for abattoirs and cold storage facilities",
      features: [
        "Connect up to 10 sensors",
        `Add more sensors for $5 AUD / sensor / month${usdParen(5)}`,
        "7 gateways",
        "Automated compliance reports (PDFs)",
        "1-year data history",
        "Multi-device support",
        "SMS alerts (50 included)",
        "Priority email support"
      ],
      cta: "Get Started",
      popular: true
    },
    enterprise: {
      name: "Enterprise",
      basePrice: isYearly ? getPrice(149) : 149,
      monthlyEquivalent: isYearly ? Math.round(149 * (1 - yearlyDiscount)) : null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: "25+",
      gatewayCount: "unlimited",
      gatewayLabel: "Unlimited gateways",
      description: "For supply chain and large-scale logistics",
      features: [
        "Connect 25+ sensors",
        "Unlimited gateways",
        `Add more sensors for $5 AUD / sensor / month${usdParen(5)}`,
        "Multi-site management",
        "API access & integrations",
        "Custom user permissions",
        "Unlimited SMS alerts",
        "Phone & priority support",
        "Custom reporting"
      ],
      cta: "Contact Sales",
      popular: false
    }
  };

  const usagePricing = [
    {
      name: "Additional Sensors",
      price: "$5 AUD / sensor / month",
      description: "Connect more sensors beyond your plan's capacity",
      example: "Business Plan (10 sensors) + 5 extra = $75 AUD + $25 AUD = $100 AUD / month",
      cta: "Add Sensors"
    },
    {
      name: "Cellular Connectivity",
      price: "$15 AUD / gateway / month",
      description: "4G/LTE data for gateways with cellular backup",
      example: "Perfect for remote locations without reliable Wi-Fi",
      cta: "Add Connectivity"
    },
    {
      name: "SMS Alerts",
      price: "$0.15 AUD / SMS",
      description: "After 50 included alerts per month",
      example: "Prevents loss from alert storms, only pay for what you use"
    }
  ];

  const faqItems = [
    {
      title: "What does 'sensor capacity' mean?",
      content: "Sensor capacity is the maximum number of sensors you can register and connect to your ChillVerify account on that plan. You can purchase sensors separately and connect them up to your plan's limit."
    },
    {
      title: "Can I buy sensors separately?",
      content: "Yes! Sensors are one-time purchases. The subscription covers the software platform to connect and monitor them. You can start with a few sensors and add more as your operation grows."
    },
    {
      title: "What if I need more sensors later?",
      content: `You can add additional sensors for $5 AUD / sensor / month${usdParen(5)}, or upgrade to a higher-capacity plan. We'll automatically calculate the best option for you.`
    },
    {
      title: "How does the ROI calculator work?",
      content: "Our calculator estimates your potential savings from prevented food spoilage versus the cost of ChillVerify. Most customers see a 200-300% ROI in the first year."
    },
    {
      title: "Is there a contract or commitment?",
      content: "No long-term contracts! You can change plans or cancel anytime. We're confident you'll see the value in preventing costly spoilage."
    },
    {
      title: "What kind of support do you offer?",
      content: "Starter plans get email support, Business plans get priority email support, and Enterprise plans get phone support with a dedicated account manager."
    },
    {
      title: "Do you offer discounts for annual billing?",
      content: "Yes! Toggle to 'Yearly' billing above to save 20% on any plan. You'll be billed once per year at the discounted rate."
    },
    {
      title: "How secure is my data?",
      content: "All data is encrypted in transit and at rest. We're GDPR compliant and follow industry best practices for data security and privacy."
    }
  ];

  return (
    <div className="pricing-page-wrapper" style={{ backgroundColor: bgPrimary, color: textPrimary, minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        .pricing-page-wrapper,
        .pricing-page-wrapper * { box-sizing: border-box; }
        .pricing-page-wrapper .cds--tile { min-width: 0; max-width: 100%; }
        .pricing-page-wrapper .cds--btn { max-width: 100%; white-space: normal; height: auto; min-height: 2.5rem; }
        .pricing-tiers-grid { width: 100%; }
        .pricing-tier-card { min-width: 0 !important; max-width: 100% !important; overflow: hidden; }
        @media (max-width: 672px) {
          .pricing-page-wrapper { overflow-x: hidden; }
          .pricing-page-wrapper .pricing-page-section { padding: 32px 16px !important; }
          .pricing-page-wrapper .pricing-billing-toggle { flex-wrap: wrap; row-gap: 12px; }
          .pricing-tier-card { padding: 20px 14px !important; }
          .pricing-tier-price { font-size: 1.75rem !important; }
          .pricing-page-wrapper h2 { font-size: 1.5rem !important; }
          .pricing-tier-card:hover { transform: none !important; }
        }
        @media (max-width: 480px) {
          .pricing-tier-price { font-size: 1.5rem !important; }
          .pricing-tier-price > span:not(:first-child) { font-size: 0.95rem !important; }
        }
      `}</style>
      {/* Hero Section */}
      <PricingHero />

      {/* Navigation Tabs */}
      <div style={{ 
        padding: '32px 0', 
        borderBottom: `1px solid ${borderColor}`,
        position: 'sticky',
        top: '0',
        backgroundColor: bgPrimary,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', width: '100%', maxWidth: '400px' }}>
              {['plans', 'usage'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    backgroundColor: activeTab === tab ? (isDark ? '#393939' : '#e0e0e0') : 'transparent',
                    color: textPrimary,
                    fontSize: '1rem',
                    fontWeight: activeTab === tab ? '600' : '400',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="pricing-page-section" style={{ padding: '64px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

            {/* Pricing Plans */}
            {activeTab === 'plans' && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                  <h2 style={{ fontSize: '2.5rem', fontWeight: '600', marginBottom: '16px' }}>
                    Choose Your Plan
                  </h2>
                  <p style={{ fontSize: '1.125rem', color: textSecondary, maxWidth: '600px', margin: '0 auto' }}>
                    Start small and scale as you grow. All plans include our core monitoring features.
                  </p>
                </div>

                {/* Billing Interval Toggle */}
                <div className="pricing-billing-toggle" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '48px' }}>
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: billingInterval === 'monthly' ? 600 : 400,
                    color: billingInterval === 'monthly' ? textPrimary : textSecondary,
                    cursor: 'pointer',
                  }} onClick={() => setBillingInterval('monthly')}>
                    Monthly
                  </span>
                  <button
                    onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
                    style={{
                      width: '56px',
                      height: '28px',
                      borderRadius: '14px',
                      border: 'none',
                      backgroundColor: billingInterval === 'yearly' ? '#0f62fe' : (isDark ? '#525252' : '#c6c6c6'),
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      padding: 0,
                    }}
                    aria-label="Toggle billing interval"
                  >
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      position: 'absolute',
                      top: '3px',
                      left: billingInterval === 'yearly' ? '31px' : '3px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </button>
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: billingInterval === 'yearly' ? 600 : 400,
                    color: billingInterval === 'yearly' ? textPrimary : textSecondary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }} onClick={() => setBillingInterval('yearly')}>
                    Yearly
                    <span style={{
                      backgroundColor: '#24a148',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '3px 10px',
                      borderRadius: '12px',
                    }}>
                      Save 20%
                    </span>
                  </span>
                </div>

                <div className="pricing-tiers-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', 
                  gap: '20px',
                  marginBottom: '64px',
                  alignItems: 'stretch',
                  width: '100%',
                }}>
                  {Object.values(pricingTiers).map((tier) => (
                    <PricingTier key={tier.name} tier={tier} />
                  ))}
                </div>

                <p style={{ fontSize: '0.85rem', color: textSecondary, maxWidth: '700px', margin: '0 auto 16px' }}>
                  * USD equivalents are estimates based on the latest available AUD→USD exchange rate. Final price, currency
                  and tax will be confirmed at checkout.
                </p>
              </div>
            )}

            {/* Usage Pricing */}
            {activeTab === 'usage' && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                  <h2 style={{ fontSize: '2.5rem', fontWeight: '600', marginBottom: '16px' }}>
                    Scale as You Grow
                  </h2>
                  <p style={{ fontSize: '1.125rem', color: textSecondary, maxWidth: '600px', margin: '0 auto' }}>
                    Usage-based pricing ensures you only pay for what you use. Perfect for growing operations.
                  </p>
                </div>

                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                  {usagePricing.map((usage, index) => (
                    <UsagePricing key={index} usage={usage} />
                  ))}
                </div>

                <p style={{ fontSize: '0.85rem', color: textSecondary, maxWidth: '700px', margin: '32px auto 0' }}>
                  * USD equivalents are indicative only. Actual charges and currency are determined by your payment provider
                  at checkout based on the prevailing exchange rate.
                </p>
              </div>
            )}

        </div>
      </div>

      {/* FAQ Section */}
      <div style={{ 
        padding: '64px 20px', 
        backgroundColor: isDark ? '#262626' : '#f4f4f4',
        borderTop: `1px solid ${borderColor}`
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '16px' }}>
                Frequently Asked Questions
              </h2>
              <p style={{ color: textSecondary }}>
                Have questions? We're here to help.
              </p>
            </div>

            <Accordion>
              {faqItems.map((item, index) => (
                <AccordionItem key={index} title={item.title}>
                  <p style={{ lineHeight: '1.6', color: textSecondary }}>
                    {item.content}
                  </p>
                </AccordionItem>
              ))}
            </Accordion>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ 
        padding: '64px 20px', 
        textAlign: 'center',
        backgroundColor: isDark ? '#161616' : '#ffffff'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '16px' }}>
              Ready to Protect Your Cold Chain?
            </h2>
            <p style={{ color: textSecondary, marginBottom: '32px', fontSize: '1.125rem' }}>
              Join hundreds of businesses preventing costly food spoilage with ChillVerify.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                kind="secondary" 
                size="lg"
                onClick={() => navigate('/contact')}
              >
                Contact Sales
              </Button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
